import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner, CustomModal } from '../components';
import { avatarsAPI, plansAPI, usersAPI } from '../services/api';
import './AvatarDetailsPage.css';

const AvatarDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingSubscriptionPlans, setLoadingSubscriptionPlans] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.info('Please sign in to view avatar details');
      navigate('/signin');
    } else {
      setIsAuthenticated(true);
      setIsAuthenticated(true);
      fetchAvatarDetails();
      fetchTokenBalance();
    }
  }, [id, navigate]);

  const fetchAvatarDetails = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds

    try {
      setLoading(true);

      // Fetch avatar details (only call needed - user data is not used on this page)
      const avatarResponse = await avatarsAPI.getAvatar(id);
      if (avatarResponse.status && avatarResponse.data) {
        setAvatar(avatarResponse.data);
        setEditFormData({
          first_name: avatarResponse.data.first_name || '',
          last_name: avatarResponse.data.last_name || ''
        });
        setLoading(false);
      } else {
        // Avatar not found - might still be processing
        if (retryCount < MAX_RETRIES) {
          // Retry if avatar might still be processing
          const delay = RETRY_DELAY * (retryCount + 1);
          console.log(`Avatar not found, retrying in ${delay}ms... (Attempt ${retryCount + 1}/${MAX_RETRIES})`);
          setTimeout(() => {
            fetchAvatarDetails(retryCount + 1);
          }, delay);
          return;
        } else {
          // Max retries reached
          toast.error('Avatar not found. It may still be processing. Please try again in a moment.');
          navigate('/chat');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching avatar details:', error);

      // Check if it's a server error and retry
      const errorStatus = error?.status || error?.statusCode;
      const isServerError = errorStatus === 503 || errorStatus === 502 || errorStatus === 504;
      const isNotFound = errorStatus === 404;

      if ((isServerError || isNotFound) && retryCount < MAX_RETRIES) {
        // Retry with exponential backoff
        const delay = RETRY_DELAY * (retryCount + 1);
        console.log(`Server error (${errorStatus}), retrying in ${delay}ms... (Attempt ${retryCount + 1}/${MAX_RETRIES})`);
        toast.info(`Avatar is still processing. Retrying... (${retryCount + 1}/${MAX_RETRIES})`, {
          toastId: 'avatar-processing-retry',
          autoClose: delay
        });
        setTimeout(() => {
          fetchAvatarDetails(retryCount + 1);
        }, delay);
        return;
      }

      // Max retries reached or non-retryable error
      if (retryCount >= MAX_RETRIES) {
        toast.error('Avatar is still being processed. Please wait a moment and refresh the page.', {
          toastId: 'avatar-processing-error',
          autoClose: 5000
        });
      } else {
        toast.error('Failed to load avatar details. Please try again.', {
          toastId: 'avatar-details-error',
          autoClose: 5000
        });
      }
      navigate('/chat');
      setLoading(false);
    }
  };

  const fetchTokenBalance = async () => {
    try {
      const response = await plansAPI.getTokenBalance();
      if (response && (response.data || response.token_balance || response.usage_percentage !== undefined)) {
        setTokenBalance(response.data || response);
      }
    } catch (error) {
      console.warn('Failed to fetch token balance:', error);
    }
  };

  const fetchSubscriptionPlans = async () => {
    setLoadingSubscriptionPlans(true);
    try {
      const response = await plansAPI.getAvatarPlans();
      console.log('Avatar Plans API response:', response);

      let extractedPlans = [];
      const data = response.data || response;

      // Handle nested structure: data.avatar_types -> plan -> plans array
      if (data && data.avatar_types && Array.isArray(data.avatar_types)) {
        // Find "for a loved one" (id 2) or combine all paid plans
        const lovedOneType = data.avatar_types.find(t => t.id === 2 || (t.name && t.name.toLowerCase().includes('loved')));

        if (lovedOneType && Array.isArray(lovedOneType.plans)) {
          extractedPlans = lovedOneType.plans;
        } else {
          // Fallback: extract all plans if specific type not found
          extractedPlans = data.avatar_types.flatMap(t => t.plans || []);
        }
      } else if (Array.isArray(data)) {
        extractedPlans = data;
      } else if (data.plans && Array.isArray(data.plans)) {
        extractedPlans = data.plans;
      }

      // Filter out free plans and ensure unique plans if needed
      const paidPlans = extractedPlans.filter(plan =>
        (plan.monthly_price_usd > 0 || plan.yearly_price_usd > 0)
      ).map(plan => {
        // Determine name and description if missing
        const interval = plan.monthly_price_usd ? 'Monthly' : 'Yearly';
        return {
          ...plan,
          name: plan.name || `${interval} Plan`,
          description: plan.description || `Premium features - Billed ${interval}`
        };
      });

      setSubscriptionPlans(paidPlans);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoadingSubscriptionPlans(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form data
      setEditFormData({
        first_name: avatar.first_name || '',
        last_name: avatar.last_name || ''
      });
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveAvatar = async () => {
    if (!editFormData.first_name.trim() || !editFormData.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    setSaving(true);
    try {
      // 1. Update Avatar Profile
      const response = await avatarsAPI.updateAvatarProfile(id, {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name
      });

      if (response || response.status) {
        toast.success('Avatar updated successfully');
        setIsEditing(false);
        fetchAvatarDetails(); // Refresh data to show updated time
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast.error('Failed to update avatar');
    } finally {
      setSaving(false);
    }
  };

  const handleSubscriptionPayment = async (plan, interval) => {
    try {
      if (!plan || !plan.id) {
        toast.error('Invalid plan selected');
        return;
      }

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment/success`;
      const currentUrl = window.location.href.split('?')[0];

      // Assuming we need to use a payment creation API similar to addons but for subscriptions
      // Using existing paymentsAPI.createPaypalOrder based on api.js inspection
      const response = await import('../services/api').then(module =>
        module.paymentsAPI.createPaypalOrder({
          avatar_plan_id: plan.id,
          plan_interval: interval,
          return_url: successUrl,
          cancel_url: currentUrl
        })
      );

      console.log('PayPal create order response:', response);

      let approveUrl = null;

      if (response?.approve_url) {
        approveUrl = response.approve_url;
      } else if (response?.data?.approve_url) {
        approveUrl = response.data.approve_url;
      } else if (response?.links && Array.isArray(response.links)) {
        const link = response.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      } else if (response?.data?.links && Array.isArray(response.data.links)) {
        const link = response.data.links.find(l => l.rel === 'approve');
        if (link) approveUrl = link.href;
      }

      if (approveUrl) {
        let orderId = response?.order_id || response?.data?.order_id;
        if (!orderId) {
          try {
            orderId = new URL(approveUrl).searchParams.get('token');
          } catch (e) { }
        }

        localStorage.setItem('paypal_checkout_context', JSON.stringify({
          order_id: orderId,
          avatar_plan_id: plan.id,
          plan_interval: interval,
          auto_renew: true,
          started_from: `/avatar/${id}`
        }));

        window.location.href = approveUrl;
      } else {
        const msg = (typeof response?.msg === 'string' ? response.msg : null) ||
          (typeof response?.message === 'string' ? response.message : null) ||
          'No approval URL found';
        console.error('No approval URL found in PayPal response:', response);
        toast.error(`Failed to initiate payment: ${msg}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      const msg = error.message || error.msg || 'Payment initialization failed';
      toast.error(msg);
    }
  };

  const getAvatarInitials = () => {
    if (!avatar) return 'A';
    const firstName = avatar.first_name || '';
    const lastName = avatar.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelation = (relation) => {
    if (!relation) return 'N/A';
    // Capitalize first letter and handle special cases
    const relationMap = {
      'self': 'Self',
      'father': 'Father',
      'mother': 'Mother',
      'spouse': 'Spouse',
      'child': 'Child',
      'wife': 'Wife',
      'husband': 'Husband',
      'son': 'Son',
      'daughter': 'Daughter',
      'granddaughter': 'Granddaughter',
      'grandfather': 'Grandfather',
      'cousin': 'Cousin',
      'uncle': 'Uncle',
      'niece': 'Niece',
      'aunt': 'Aunt',
      'other': 'Other'
    };
    return relationMap[relation.toLowerCase()] || relation.charAt(0).toUpperCase() + relation.slice(1);
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="avatar-details-page">
        <Navbar />
        <LoadingSpinner fullScreen text="Loading avatar details..." />
      </div>
    );
  }

  if (!avatar) {
    return (
      <div className="avatar-details-page">
        <Navbar />
        <Container className="avatar-details-container">
          <div className="avatar-details-error">
            <h2>Avatar not found</h2>
            <p>The avatar you're looking for doesn't exist or you don't have access to it.</p>
            <CustomButton variant="primary" onClick={() => navigate('/chat')}>
              Back to Dashboard
            </CustomButton>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="avatar-details-page">
      <Navbar />

      <Container className="avatar-details-container">
        <Link to="/chat" className="back-link">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </Link>

        <div className="avatar-details-content">
          <Row>
            <Col lg={4}>
              <div className="avatar-details-card">
                <div className="avatar-details-avatar-circle gradient-sage">
                  {avatar.profile_image_url ? (
                    <img src={avatar.profile_image_url} alt="Avatar Profile" className="avatar-profile-image" />
                  ) : (
                    getAvatarInitials()
                  )}
                </div>
                <h2>{`${avatar.first_name || ''} ${avatar.last_name || ''}`.trim() || 'Avatar'}</h2>
                {avatar.status === 'active' && (
                  <div className="avatar-details-status">
                    <span className="status-badge status-active">
                      ● Active
                    </span>
                  </div>
                )}
              </div>
            </Col>

            <Col lg={8}>
              <div className="avatar-details-info-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0 }}>Information</h3>
                  {!isEditing ? (
                    <button
                      onClick={handleEditToggle}
                      className="icon-btn"
                      title="Edit Details"
                      style={{ color: 'var(--soft-teal)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={handleEditToggle}
                        className="text-btn"
                        style={{ color: 'var(--slate)', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveAvatar}
                        className="text-btn"
                        style={{ color: 'var(--soft-teal)', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="details-grid">
                  <div className="detail-item">
                    <div className="detail-label">First Name</div>
                    <div className="detail-value">
                      {isEditing ? (
                        <Form.Control
                          type="text"
                          name="first_name"
                          value={editFormData.first_name}
                          onChange={handleInputChange}
                          className="edit-input"
                          style={{ padding: '4px 8px', fontSize: '15px' }}
                        />
                      ) : (
                        avatar.first_name || 'N/A'
                      )}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Last Name</div>
                    <div className="detail-value">
                      {isEditing ? (
                        <Form.Control
                          type="text"
                          name="last_name"
                          value={editFormData.last_name}
                          onChange={handleInputChange}
                          className="edit-input"
                          style={{ padding: '4px 8px', fontSize: '15px' }}
                        />
                      ) : (
                        avatar.last_name || 'N/A'
                      )}
                    </div>
                  </div>
                  {/* Avatar Type removed */}
                  {avatar.relation && (
                    <div className="detail-item">
                      <div className="detail-label">Relation</div>
                      <div className="detail-value">{formatRelation(avatar.relation)}</div>
                    </div>
                  )}
                  <div className="detail-item">
                    <div className="detail-label">Created At</div>
                    <div className="detail-value">{formatDate(avatar.created_at)}</div>
                  </div>
                  {/* Last Updated removed */}
                  {avatar.confidence && (
                    <div className="detail-item">
                      <div className="detail-label">Confidence Score</div>
                      <div className="detail-value">{avatar.confidence}%</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Plan Details and Usage Details Side by Side */}
              <Row>
                <Col lg={6} className="mb-3 mb-lg-0">
                  {/* Plan Details Section */}
                  {(() => {
                    const isLovedOne = avatar.avatar_type_id === 2 ||
                      (avatar.avatar_type && avatar.avatar_type.toLowerCase().includes('loved'));

                    if (isLovedOne) {
                      const currentPlan = avatar.plan_interval || 'yearly';
                      return (
                        <div className="avatar-details-info-card">
                          <h3>Plan Details</h3>
                          <div className="details-grid">
                            <div className="detail-item">
                              <div className="detail-label">Billing Period</div>
                              <div className="detail-value">
                                {currentPlan === 'yearly' ? 'Yearly' : 'Monthly'}
                              </div>
                            </div>
                            <div className="detail-item">
                              <div className="detail-label">Price</div>
                              <div className="detail-value">
                                {currentPlan === 'yearly' ? '$149/year' : '$19.99/month'}
                              </div>
                            </div>
                            {avatar.auto_renew !== undefined && (
                              <div className="detail-item">
                                <div className="detail-label">Auto-Renew</div>
                                <div className="detail-value">
                                  {avatar.auto_renew ? 'Enabled' : 'Disabled'}
                                </div>
                              </div>
                            )}
                            {avatar.plan_price && (
                              <div className="detail-item">
                                <div className="detail-label">Plan Price</div>
                                <div className="detail-value">${avatar.plan_price}</div>
                              </div>
                            )}
                          </div>
                          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                            <CustomButton
                              variant="primary"
                              onClick={() => {
                                setShowSubscriptionModal(true);
                                fetchSubscriptionPlans();
                              }}
                              className="w-100"
                            >
                              Upgrade Plan
                            </CustomButton>
                          </div>
                        </div>
                      );
                    } else if (!isLovedOne) {
                      return (
                        <div className="avatar-details-info-card">
                          <h3>Plan Details</h3>
                          <div className="details-grid">
                            <div className="detail-item" style={{ gridColumn: 'span 2' }}>
                              <div className="detail-label">Plan Type</div>
                              <div className="detail-value" style={{ whiteSpace: 'nowrap' }}>Free for 14 days</div>
                            </div>
                          </div>
                          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                            <CustomButton
                              variant="primary"
                              onClick={() => {
                                setShowSubscriptionModal(true);
                                fetchSubscriptionPlans();
                              }}
                              className="w-100"
                            >
                              Upgrade Plan
                            </CustomButton>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </Col>

                <Col lg={6}>
                  {/* Usage Details */}
                  <div className="avatar-details-info-card">
                    <h3>Usage Details</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <div className="detail-label">Messages Used</div>
                        <div className="detail-value">
                          {(() => {
                            // If we have token balance from API, use that percentage
                            if (tokenBalance && tokenBalance.usage_percentage !== undefined) {
                              return `${tokenBalance.usage_percentage}% Used`;
                            }
                            // Fallback to calculation if no token balance API data but we have avatar data
                            const messagesUsed = avatar.messages_used || avatar.messages_count || 0;
                            const messagesLimit = avatar.messages_limit || avatar.total_messages || 500;
                            const percentage = messagesLimit > 0 ? Math.min(100, (messagesUsed / messagesLimit) * 100).toFixed(2) : 0;
                            return `${percentage}% Used`;
                          })()}
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-label">Storage Used</div>
                        <div className="detail-value">
                          {(() => {
                            console.log('Avatar Data for Storage:', {
                              remaining_bytes: avatar.remaining_storage_bytes,
                              used_mb: avatar.storage_used_mb,
                              used_raw: avatar.storage_used,
                              limit_gb: avatar.storage_limit_gb,
                              limit_raw: avatar.storage_limit
                            });
                            let mbUsed = parseFloat(avatar.storage_used_mb || avatar.storage_used || 0);

                            // If mbUsed is 0, try to calculate from remaining bytes
                            if (!mbUsed && avatar.remaining_storage_bytes) {
                              // Default to 5GB if no limit specified (standard plan)
                              const limitGb = parseFloat(avatar.storage_limit_gb || avatar.storage_limit || 5);
                              const totalBytes = limitGb * 1024 * 1024 * 1024;
                              const usedBytes = Math.max(0, totalBytes - parseFloat(avatar.remaining_storage_bytes));
                              mbUsed = usedBytes / (1024 * 1024);
                            }

                            if (mbUsed >= 1024) {
                              return `${(mbUsed / 1024).toFixed(2)} GB`;
                            }
                            return `${mbUsed.toFixed(2)} MB`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>


            </Col>
          </Row>
        </div>
      </Container>

      {/* Upgrade Plan Modal */}
      {/* Subscription Plan Modal */}
      <CustomModal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        title="Upgrade Plan"
        subtitle="Choose a plan that works best for you."
        iconVariant="gold"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        }
        size="lg"
      >
        <div style={{ padding: '20px 0' }}>
          {loadingSubscriptionPlans ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <LoadingSpinner size="sm" />
              <p style={{ marginTop: '10px', color: 'var(--slate)' }}>Loading available plans...</p>
            </div>
          ) : subscriptionPlans.length > 0 ? (
            <div className="plans-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {subscriptionPlans.map((plan) => (
                <div key={plan.id} style={{
                  background: 'white',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '16px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: '700', color: 'var(--soft-black)' }}>
                      {plan.name}
                    </h5>
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--slate)', minHeight: '40px' }}>
                      {plan.description || 'Unlock premium features'}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'auto' }}>
                    {plan.monthly_price_usd > 0 && (
                      <button
                        onClick={() => handleSubscriptionPayment(plan, 'monthly')}
                        style={{
                          background: 'white',
                          color: 'var(--soft-teal)',
                          border: '2px solid var(--soft-teal)',
                          padding: '12px',
                          borderRadius: '12px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        ${plan.monthly_price_usd}/month
                      </button>
                    )}

                    {plan.yearly_price_usd > 0 && (
                      <button
                        onClick={() => handleSubscriptionPayment(plan, 'yearly')}
                        style={{
                          background: 'var(--soft-teal)',
                          color: 'white',
                          border: '2px solid var(--soft-teal)',
                          padding: '12px',
                          borderRadius: '12px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        ${plan.yearly_price_usd}/year
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--slate)' }}>
              No upgrade plans available at the moment.
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default AvatarDetailsPage;

